import { COMPANIES } from 'constants/Entities';
import { getCrudDataFromBackendForm } from 'utils/copilotRegistry/crudForm';
import { getLiteral } from 'utils/getLiteral';

import { LEADS_LITERALS } from './literals';

const pickSingularPlural = (count, singularKey, pluralKey) =>
    Number(count) === 1 ? singularKey : pluralKey;

/**
 * @typedef {{ bold?: string, text?: string }} ReviewCopySegment
 */

/**
 * Review-step title and stats as plain text segments (numbers rendered bold in UI).
 * @returns {{ titleSegments: ReviewCopySegment[], statsSegments: ReviewCopySegment[] }}
 */
export const buildReviewSummarySegments = ({
    readyCount,
    createdCount,
    failedCount,
    creditsUsed,
    balance,
}) => {
    const balanceText = String(balance);

    if (readyCount === 0 && createdCount === 0 && failedCount > 0) {
        return {
            titleSegments: [
                { bold: String(failedCount) },
                {
                    text: getLiteral(
                        pickSingularPlural(
                            failedCount,
                            LEADS_LITERALS.reviewTitleAllFailedSuffixSingular,
                            LEADS_LITERALS.reviewTitleAllFailedSuffixPlural,
                        ),
                    ),
                },
            ],
            statsSegments: [
                { bold: String(failedCount) },
                { text: getLiteral(LEADS_LITERALS.reviewStatsAllFailedMiddle) },
                { bold: balanceText },
            ],
        };
    }

    if (readyCount === 0 && createdCount > 0 && failedCount === 0) {
        return {
            titleSegments: [{ text: getLiteral(LEADS_LITERALS.reviewTitleAllReviewed) }],
            statsSegments: [
                { bold: String(creditsUsed) },
                { text: getLiteral(LEADS_LITERALS.reviewStatsAllReviewedMiddle) },
                { bold: balanceText },
            ],
        };
    }

    if (readyCount === 0 && createdCount > 0 && failedCount > 0) {
        return {
            titleSegments: [
                { text: getLiteral(LEADS_LITERALS.reviewTitleAllReviewedWithFailuresPrefix) },
                { bold: String(failedCount) },
                { text: getLiteral(LEADS_LITERALS.reviewTitleAllReviewedWithFailuresSuffix) },
            ],
            statsSegments: [
                { bold: String(createdCount) },
                { text: getLiteral(LEADS_LITERALS.reviewStatsAllReviewedWithFailuresAfterCreated) },
                { bold: String(failedCount) },
                {
                    text: getLiteral(LEADS_LITERALS.reviewStatsAllReviewedWithFailuresAfterFailed),
                },
                { bold: String(creditsUsed) },
                {
                    text: getLiteral(LEADS_LITERALS.reviewStatsAllReviewedWithFailuresAfterCredits),
                },
                { bold: balanceText },
            ],
        };
    }

    if (failedCount > 0) {
        return {
            titleSegments: [
                { bold: String(readyCount) },
                {
                    text: getLiteral(
                        pickSingularPlural(
                            readyCount,
                            LEADS_LITERALS.reviewTitleWithFailuresReadySuffixSingular,
                            LEADS_LITERALS.reviewTitleWithFailuresReadySuffixPlural,
                        ),
                    ),
                },
                { bold: String(failedCount) },
                {
                    text: getLiteral(
                        pickSingularPlural(
                            failedCount,
                            LEADS_LITERALS.reviewTitleWithFailuresFailedSuffixSingular,
                            LEADS_LITERALS.reviewTitleWithFailuresFailedSuffixPlural,
                        ),
                    ),
                },
            ],
            statsSegments: [
                { bold: String(readyCount) },
                { text: getLiteral(LEADS_LITERALS.reviewStatsWithFailuresAfterReady) },
                { bold: String(failedCount) },
                { text: getLiteral(LEADS_LITERALS.reviewStatsWithFailuresAfterFailed) },
                { bold: String(creditsUsed) },
                { text: getLiteral(LEADS_LITERALS.reviewStatsWithFailuresAfterCredits) },
                { bold: balanceText },
            ],
        };
    }

    return {
        titleSegments: [
            { bold: String(readyCount) },
            {
                text: getLiteral(
                    pickSingularPlural(
                        readyCount,
                        LEADS_LITERALS.reviewTitleReadySuffixSingular,
                        LEADS_LITERALS.reviewTitleReadySuffixPlural,
                    ),
                ),
            },
        ],
        statsSegments: [
            { bold: String(readyCount) },
            {
                text: getLiteral(
                    pickSingularPlural(
                        readyCount,
                        LEADS_LITERALS.reviewStatsNoFailuresLeadSuffixSingular,
                        LEADS_LITERALS.reviewStatsNoFailuresLeadSuffixPlural,
                    ),
                ),
            },
            { bold: String(creditsUsed) },
            { text: getLiteral(LEADS_LITERALS.reviewStatsNoFailuresAfterCredits) },
            { bold: balanceText },
        ],
    };
};

export const buildPreviewRequest = ({
    category,
    maxResults,
    radiusKm,
    origin,
    includeDuplicates,
}) => ({
    lat: String(origin.lat),
    lng: String(origin.lng),
    radiusMeters: String(Math.round(Number(radiusKm) * 1000)),
    maxResults: String(maxResults),
    query: category || '',
    includePotentialDuplicates: Boolean(includeDuplicates),
});

export const splitByBucket = (leads = []) => ({
    new: leads.filter((lead) => !lead.potential_duplicate),
    duplicates: leads.filter((lead) => lead.potential_duplicate),
});

export const getSelectedLeads = (leads = [], selectedIds = []) => {
    const ids = new Set(selectedIds);
    return leads.filter((lead) => ids.has(lead.id));
};

export const toAccountCrudData = (row) => getCrudDataFromBackendForm(COMPANIES.trueName, row?.form);

export const isSameReviewRowId = (row, rowId) => {
    if (rowId == null || rowId === '') return false;
    const target = String(rowId);
    return String(row?.id) === target || String(row?.sourceLead?.id) === target;
};

export const markReviewRowAsCreated = (rows, rowId, creationDate) =>
    rows.map((row) =>
        isSameReviewRowId(row, rowId) ? { ...row, status: 'created', creationDate } : row,
    );
